// The game header's tabs (Jared, 11/2: the painted header belongs to the game, not the site). The city page registers
// what each tab does while it is open; the header (components/layout/KitHeader) shows them and lights the open one.
import { useSyncExternalStore } from 'react'

export type GameTab = 'explore' | 'party' | 'inventory' | 'journal' | 'evolve'
export const GAME_TABS: readonly GameTab[] = ['explore', 'party', 'inventory', 'journal', 'evolve']
/** What a tab does, and whether it is the open one now. */
export type GameNav = Record<GameTab, { go: () => void; on: boolean }>

let nav: GameNav | null = null
const subs = new Set<() => void>()
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }
export const getGameNav = (): GameNav | null => nav
export const useGameNav = (): GameNav | null => useSyncExternalStore(subscribe, getGameNav, getGameNav)
/** The city page says what the tabs do (again whenever their state changes); the returned function takes them away. */
export function registerGameNav(n: GameNav): () => void {
  nav = n; subs.forEach((f) => f())
  return () => { if (nav === n) { nav = null; subs.forEach((f) => f()) } }
}

// opening a section of the side panel: the shell (ThreeShell, PhoneShell) opens its panel or sheet and brings the
// section into view. Sections carry `data-game-section="<tab>"`.
const PANEL_EVENT = 'city:panel-section'
export function openPanelSection(tab: GameTab): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(PANEL_EVENT, { detail: tab }))
  // once the panel is open, scroll to the section (a frame later, after the sheet has laid out)
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const el = document.querySelector(`[data-game-section="${tab}"]`)
    if (!el) return
    // a section folded away (the phone menu's More, a closed details) unfolds first
    for (let p: Element | null = el; p; p = p.parentElement) if (p instanceof HTMLDetailsElement) p.open = true
    el.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }))
}
/** For the shells: open the panel when a tab asks for a section of it. */
export function onPanelSection(f: (tab: GameTab) => void): () => void {
  const h = (e: Event) => f((e as CustomEvent<GameTab>).detail)
  window.addEventListener(PANEL_EVENT, h)
  return () => window.removeEventListener(PANEL_EVENT, h)
}
